import { useState } from "react";
import { Brain, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "@/components/ui/use-toast";
import { extractErrorMessage } from "@/api/client";
import { formatDate } from "@/lib/format";
import { useDebounce } from "@/lib/useDebounce";
import {
  useNaucenePriradenia,
  useZmazatPriradenie,
  type DruhMapovania,
} from "@/api/hooks/useTopsolidRaw";

/**
 * Čo si appka pamätá z importov TopSolidu.
 *
 * Pamäť je zmysel KOLA 80 — druhý import tej istej zákazky sa nepýta na
 * nič. Pamäť, ktorú sa nedá prezrieť a opraviť, je ale pasca: raz zle
 * priradený materiál by sa ticho opakoval pri každom ďalšom importe
 * a nebolo by ako to zvrátiť. Zmazané priradenie sa pri ďalšom importe
 * jednoducho spýta znova.
 */

const DRUHY: { kluc: "" | DruhMapovania; label: string }[] = [
  { kluc: "", label: "Všetko" },
  { kluc: "material", label: "Materiály" },
  { kluc: "hrana", label: "Hrany" },
  { kluc: "kovanie", label: "Kovania" },
];

export function NaucenePriradeniaSection(): JSX.Element {
  const [druh, setDruh] = useState<"" | DruhMapovania>("");
  const [hladanie, setHladanie] = useState("");
  const q = useDebounce(hladanie, 300);
  const query = useNaucenePriradenia({
    druh: druh || undefined,
    q: q || undefined,
  });
  const zmazat = useZmazatPriradenie();

  const riadky = query.data?.data ?? [];

  async function zmaz(id: string, popis: string): Promise<void> {
    if (!window.confirm(`Zabudnúť priradenie „${popis}“?`)) return;
    try {
      await zmazat.mutateAsync(id);
      toast({ title: "Priradenie zabudnuté" });
    } catch (e) {
      toast({
        variant: "destructive",
        title: "Nezmazané",
        description: extractErrorMessage(e),
      });
    }
  }

  return (
    <section className="space-y-3 rounded-lg border bg-card p-4">
      <div>
        <h2 className="flex items-center gap-2 text-xl">
          <Brain className="h-5 w-5 text-primary" />
          Zapamätané priradenia z TopSolidu
        </h2>
        <p className="text-xs text-muted-foreground">
          Čo si appka zapamätala pri importoch — názov z TopSolidu a položka
          cenníka, na ktorú ho priradzuje. Zmazané priradenie sa pri ďalšom
          importe spýta znova.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {DRUHY.map((d) => (
          <Button
            key={d.kluc || "vsetko"}
            type="button"
            size="sm"
            variant={druh === d.kluc ? "default" : "outline"}
            onClick={() => setDruh(d.kluc)}
          >
            {d.label}
          </Button>
        ))}
        <Input
          className="h-9 w-64"
          value={hladanie}
          onChange={(e) => setHladanie(e.target.value)}
          placeholder="Hľadať názov z TopSolidu alebo materiál…"
          aria-label="Hľadať v naučených priradeniach"
        />
      </div>

      {query.isLoading ? (
        <p className="text-sm text-muted-foreground">Načítavam…</p>
      ) : riadky.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          {q || druh
            ? "Nič nesedí na hľadanie."
            : "Zatiaľ nič — pamäť sa plní pri importoch z TopSolidu."}
        </p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Druh</TableHead>
              <TableHead>Z TopSolidu</TableHead>
              <TableHead>Priradené na</TableHead>
              <TableHead className="text-right">Použité</TableHead>
              <TableHead>Naposledy</TableHead>
              <TableHead className="w-10" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {riadky.map((r) => {
              const popis = r.kontext ? `${r.token} [${r.kontext}]` : r.token;
              return (
                <TableRow key={r.id}>
                  <TableCell className="text-xs text-muted-foreground">
                    {r.druh}
                  </TableCell>
                  <TableCell className="font-mono text-xs">{popis}</TableCell>
                  <TableCell className="text-sm">
                    {r.zmazat ? (
                      /* „Neimportovať" je plnohodnotné priradenie — bez
                         neho by sa appka pýtala na kolíky pri každom
                         importe */
                      <Badge variant="secondary">nenahrávať</Badge>
                    ) : (
                      <>
                        <span className="font-mono text-xs">
                          {r.material_kod ?? "—"}
                        </span>
                        {r.material_nazov ? (
                          <span className="ml-2 text-muted-foreground">
                            {r.material_nazov}
                          </span>
                        ) : null}
                      </>
                    )}
                  </TableCell>
                  <TableCell className="text-right text-sm">
                    {r.pocet_pouziti}×
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {formatDate(r.naposledy_at)}
                  </TableCell>
                  <TableCell>
                    <Button
                      size="sm"
                      variant="ghost"
                      aria-label={`Zabudnúť priradenie ${popis}`}
                      onClick={() => void zmaz(r.id, popis)}
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
    </section>
  );
}
